/**
 * Map catalog-style city labels (same naming vibe as hotel search) to Duffel slice codes.
 * Metro/airport IATA codes — Duffel accepts 3-letter airport or city codes.
 */

const RAW_PAIRS = `
London|GB=LON
London|UK=LON
Manchester|GB=MAN
Birmingham|GB=BHX
Edinburgh|GB=EDI
Glasgow|GB=GLA
Dublin|IE=DUB
Paris|FR=PAR
Nice|FR=NCE
Lyon|FR=LYS
Amsterdam|NL=AMS
Brussels|BE=BRU
Zurich|CH=ZRH
Geneva|CH=GVA
Frankfurt|DE=FRA
Munich|DE=MUC
Berlin|DE=BER
Hamburg|DE=HAM
Cologne|DE=CGN
Dusseldorf|DE=DUS
Vienna|AT=VIE
Madrid|ES=MAD
Barcelona|ES=BCN
Seville|ES=SVQ
Lisbon|PT=LIS
Porto|PT=OPO
Rome|IT=ROM
Milan|IT=MIL
Venice|IT=VCE
Naples|IT=NAP
Athens|GR=ATH
Istanbul|TR=IST
Dubai|AE=DXB
Abu Dhabi|AE=AUH
Doha|QA=DOH
Riyadh|SA=RUH
Jeddah|SA=JED
Tel Aviv|IL=TLV
Cairo|EG=CAI
Casablanca|MA=CMN
Marrakech|MA=RAK
Johannesburg|ZA=JNB
Cape Town|ZA=CPT
Nairobi|KE=NBO
New York|US=NYC
Los Angeles|US=LAX
Chicago|US=ORD
Miami|US=MIA
San Francisco|US=SFO
Boston|US=BOS
Washington|US=WAS
Atlanta|US=ATL
Dallas|US=DFW
Seattle|US=SEA
Denver|US=DEN
Las Vegas|US=LAS
Orlando|US=MCO
Phoenix|US=PHX
Houston|US=IAH
Toronto|CA=YYZ
Vancouver|CA=YVR
Montreal|CA=YUL
Calgary|CA=YYC
Mexico City|MX=MEX
Cancun|MX=CUN
Guadalajara|MX=GDL
Sao Paulo|BR=SAO
Rio de Janeiro|BR=RIO
Buenos Aires|AR=BUE
Lima|PE=LIM
Bogota|CO=BOG
Santiago|CL=SCL
Singapore|SG=SIN
Bangkok|TH=BKK
Phuket|TH=HKT
Chiang Mai|TH=CNX
Kuala Lumpur|MY=KUL
Jakarta|ID=CGK
Bali|ID=DPS
Manila|PH=MNL
Hong Kong|HK=HKG
Taipei|TW=TPE
Tokyo|JP=TYO
Osaka|JP=OSA
Kyoto|JP=UKY
Seoul|KR=SEL
Shanghai|CN=SHA
Beijing|CN=BJS
Guangzhou|CN=CAN
Shenzhen|CN=SZX
Chengdu|CN=CTU
Xi'an|CN=SIA
Sydney|AU=SYD
Melbourne|AU=MEL
Brisbane|AU=BNE
Perth|AU=PER
Auckland|NZ=AKL
Christchurch|NZ=CHC
Delhi|IN=DEL
Mumbai|IN=BOM
Bengaluru|IN=BLR
Chennai|IN=MAA
Hyderabad|IN=HYD
Kolkata|IN=CCU
Muscat|OM=MCT
Kuwait|KW=KWI
Manama|BH=BAH
Amman|JO=AMM
Beirut|LB=BEY
`

function parseRaw() {
  const map = {};
  for (const line of RAW_PAIRS.split('\n')) {
    const trimmed = line.trim();
    if (!trimmed || trimmed.startsWith('#')) continue;
    const eq = trimmed.indexOf('=');
    if (eq <= 0) continue;
    const key = trimmed.slice(0, eq).trim().toLowerCase();
    const code = trimmed.slice(eq + 1).trim().toUpperCase();
    if (key && /^[A-Z]{3}$/.test(code)) map[key] = code;
  }
  return map;
}

const PLACE_TO_IATA = parseRaw();

/** Map common country names (as shown after comma in labels) to ISO2 — best-effort for free text. */
const COUNTRY_NAME_TO_CC = {
  'united kingdom': 'GB',
  uk: 'GB',
  england: 'GB',
  scotland: 'GB',
  wales: 'GB',
  'united states': 'US',
  usa: 'US',
  america: 'US',
  uae: 'AE',
  'united arab emirates': 'AE',
  'south korea': 'KR',
  korea: 'KR',
  'czech republic': 'CZ',
  ukraine: 'UA',
}

function normalizeCityName(name) {
  return (name || '')
    .toString()
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
}

function normalizeCountryHint(hint) {
  const h = (hint || '').toString().trim().toLowerCase();
  if (!h) return '';
  if (h.length === 2 && /^[a-z]{2}$/i.test(h)) return h.toUpperCase();
  return COUNTRY_NAME_TO_CC[h] || '';
}

export function makeFlightPlaceKey(name, countryCodeOrHint) {
  const city = normalizeCityName(name);
  let cc = (countryCodeOrHint || '').toString().trim();
  if (cc.length === 2 && /^[a-zA-Z]{2}$/.test(cc)) {
    cc = cc.toLowerCase();
  } else {
    const mapped = normalizeCountryHint(countryCodeOrHint);
    cc = mapped ? mapped.toLowerCase() : '';
  }
  if (!city) return '';
  return cc ? `${city}|${cc}` : city;
}

/**
 * @param {object} city — catalog / locationSearchIndex city shape
 * @returns {string} 3-letter code or ''
 */
export function resolveIataFromCatalogCity(city) {
  if (!city || typeof city !== 'object') return '';
  const name = (city.name || city.cityName || city.city || '').toString().trim();
  const cc = (city.countryCode || '').toString().trim();
  const key = makeFlightPlaceKey(name, cc);
  if (key.includes('|') && PLACE_TO_IATA[key]) return PLACE_TO_IATA[key];

  const raw = (city.code || '').toString().trim();
  if (/^[A-Za-z]{3}$/.test(raw)) return raw.toUpperCase();

  return '';
}

/**
 * Try to resolve user-typed text: explicit 3-letter code, or "City, Country".
 * @returns {string}
 */
export function resolveIataFromFreeText(text) {
  const t = (text || '').trim();
  if (!t) return '';
  if (/^[A-Za-z]{3}$/.test(t)) return t.toUpperCase();

  const comma = t.indexOf(',');
  if (comma > 0) {
    const cityPart = t.slice(0, comma).trim();
    const countryPart = t.slice(comma + 1).trim();
    const key = makeFlightPlaceKey(cityPart, countryPart);
    if (key.includes('|') && PLACE_TO_IATA[key]) return PLACE_TO_IATA[key];
  }

  const lower = t.toLowerCase();
  for (const [k, code] of Object.entries(PLACE_TO_IATA)) {
    if (k.startsWith(`${lower}|`)) return code;
  }

  return '';
}

function titleCaseWords(str) {
  return (str || '')
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

export function defaultDisplayForCode(code) {
  const c = (code || '').trim().toUpperCase();
  if (!/^[A-Z]{3}$/.test(c)) return '';
  let regionNames;
  try {
    regionNames = new Intl.DisplayNames(['en'], { type: 'region' });
  } catch {
    regionNames = null;
  }
  for (const [k, v] of Object.entries(PLACE_TO_IATA)) {
    if (v === c) {
      const [city, cc] = k.split('|');
      if (!city) continue;
      const title = titleCaseWords(city);
      if (cc && regionNames) {
        try {
          const countryLabel = regionNames.of(cc.toUpperCase());
          return countryLabel ? `${title}, ${countryLabel}` : `${title}, ${cc.toUpperCase()}`;
        } catch {
          return `${title}, ${cc.toUpperCase()}`;
        }
      }
      return cc ? `${title}, ${cc.toUpperCase()}` : title;
    }
  }
  return c;
}
